import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X, Send, Loader2, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { trackDemoModalOpen, trackDemoRequestSubmit } from "@/utils/tracking";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export const DemoRequestModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [hasTrackedOpen, setHasTrackedOpen] = useState(false);

  // Track modal open only once per open
  useEffect(() => {
    if (isOpen && !hasTrackedOpen) {
      trackDemoModalOpen();
      setHasTrackedOpen(true);
    }
    if (!isOpen) {
      setHasTrackedOpen(false);
    }
  }, [isOpen, hasTrackedOpen]);

  const roles = [
    { value: "consultant", label: t('demoForm.roles.consultant') },
    { value: "company", label: t('demoForm.roles.company') },
    { value: "association", label: t('demoForm.roles.association') },
    { value: "projectOwner", label: t('demoForm.roles.projectOwner') },
    { value: "forestOwner", label: t('demoForm.roles.forestOwner') },
    { value: "other", label: t('demoForm.roles.other') },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/demo-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) throw new Error("Request failed");

      // Track successful submission
      trackDemoRequestSubmit();
      setIsSuccess(true);
      setTimeout(() => {
        onClose();
        setIsSuccess(false);
        setFormData({ name: "", email: "", company: "", role: "", message: "" });
      }, 3000);
    } catch (err) {
      setError(t('demoForm.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="demo-modal"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-100">
          <div>
            <h2 
              className="text-xl font-semibold text-stone-900"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {t('demoForm.headline')}
            </h2>
            <p className="text-sm text-stone-500 mt-1">
              {t('demoForm.description')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
            data-testid="demo-modal-close"
          >
            <X className="w-5 h-5 text-stone-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isSuccess ? (
            <div className="text-center py-8" data-testid="demo-form-success">
              <div className="w-16 h-16 rounded-full bg-[#1B4D3E]/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-[#1B4D3E]" />
              </div>
              <p className="text-lg font-medium text-stone-900">
                {t('demoForm.success')}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">
                  {t('demoForm.fields.name')} *
                </label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="h-10 rounded-lg border-stone-200"
                  data-testid="demo-form-name"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">
                  {t('demoForm.fields.email')} *
                </label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="h-10 rounded-lg border-stone-200"
                  data-testid="demo-form-email"
                />
              </div>

              {/* Company */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">
                  {t('demoForm.fields.company')}
                </label>
                <Input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="h-10 rounded-lg border-stone-200"
                  data-testid="demo-form-company"
                />
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">
                  {t('demoForm.fields.role')} *
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                  className="w-full h-10 px-3 rounded-lg border border-stone-200 bg-white text-stone-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4D3E] focus:border-transparent"
                  data-testid="demo-form-role"
                >
                  <option value="">Bitte wählen...</option>
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">
                  {t('demoForm.fields.message')}
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-stone-200 bg-white text-stone-900 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#1B4D3E] focus:border-transparent"
                  data-testid="demo-form-message"
                />
              </div>

              {/* Error */}
              {error && (
                <p className="text-sm text-red-600" data-testid="demo-form-error">
                  {error}
                </p>
              )}

              {/* Privacy Notice */}
              <p className="text-xs text-stone-500">
                {t('demoForm.privacy')}
              </p>

              {/* Submit */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#1B4D3E] hover:bg-[#163D32] text-white h-11 rounded-lg font-medium"
                data-testid="demo-form-submit"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('demoForm.submitting')}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {t('demoForm.submit')}
                  </>
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
